import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import type { TrainingPlan, EditingTrainingPlan, Routine, User, PlanContext } from '../types';
import { getLocalDateString, getTrainingPlanWeekNumber, parseLocalDate } from '../utils/dateUtils';
import { isDemoId } from '../data/mockData';
import { OFFLINE_CACHE_KEYS, readCache, type LoadSource, writeCache } from '../utils/offlineCache';

export function useTrainingPlans(
  user: User | null,
  showNotification: (msg: string, type: 'success' | 'error') => void,
  showConfirm: (msg: string, onConfirm: () => void) => void
) {
  const [trainingPlans, setTrainingPlans] = useState<TrainingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [editingPlan, setEditingPlan] = useState<EditingTrainingPlan | null>(null);

  const loadTrainingPlans = useCallback(async (): Promise<LoadSource> => {
    if (!user) return 'empty';
    setPlansLoading(true);
    try {
      const { data, error } = await supabase
        .from('training_plans')
        .select(`
          id,
          user_id,
          name,
          days_per_week,
          duration_weeks,
          status,
          start_date,
          created_at,
          updated_at,
          training_plan_days (
            id,
            plan_id,
            day_number,
            routine_id,
            label
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const mapped: TrainingPlan[] = (data || []).map((plan: any) => ({
        id: plan.id,
        user_id: plan.user_id,
        name: plan.name,
        days_per_week: plan.days_per_week,
        duration_weeks: plan.duration_weeks,
        status: plan.status,
        start_date: plan.start_date,
        created_at: plan.created_at,
        updated_at: plan.updated_at,
        days: (plan.training_plan_days || [])
          .slice()
          .sort((a: any, b: any) => (a.day_number ?? 0) - (b.day_number ?? 0))
          .map((d: any) => ({
            id: d.id,
            plan_id: d.plan_id,
            day_number: d.day_number,
            routine_id: String(d.routine_id),
            label: d.label,
          })),
      }));

      setTrainingPlans(mapped);
      await writeCache(OFFLINE_CACHE_KEYS.plans(user.id), mapped, user.id);
      return 'network';
    } catch (error) {
      console.error('Error cargando planes:', error);
      const cached = await readCache<TrainingPlan[]>(OFFLINE_CACHE_KEYS.plans(user.id), user.id);
      if (cached) {
        setTrainingPlans(cached);
        return 'cache';
      }
      return 'empty';
    } finally {
      setPlansLoading(false);
    }
  }, [user]);

  const createPlan = () => {
    setEditingPlan({
      name: '',
      days_per_week: 3,
      duration_weeks: 8,
      days: [
        { day_number: 1, routine_id: '' },
        { day_number: 2, routine_id: '' },
        { day_number: 3, routine_id: '' },
      ],
    });
  };

  const savePlan = async (onSuccess: () => Promise<void>) => {
    if (!editingPlan || !user) return;

    const planName = editingPlan.name.trim();
    if (!planName) {
      showNotification('El nombre del plan es obligatorio', 'error');
      return;
    }
    if (planName.length > 50) {
      showNotification('El nombre del plan es demasiado largo', 'error');
      return;
    }

    if (editingPlan.days.length !== editingPlan.days_per_week) {
      showNotification('La cantidad de días no coincide con los días por semana', 'error');
      return;
    }

    const hasEmptyRoutine = editingPlan.days.some(d => !d.routine_id);
    if (hasEmptyRoutine) {
      showNotification('Asigná una rutina a cada día', 'error');
      return;
    }

    const hasDemoRoutine = editingPlan.days.some(d => isDemoId(d.routine_id));
    if (hasDemoRoutine) {
      showNotification('Las rutinas demo no se pueden usar en planes', 'error');
      return;
    }

    try {
      let planId = editingPlan.id;
      const planPayload = {
        user_id: user.id,
        name: planName,
        days_per_week: editingPlan.days_per_week,
        duration_weeks: editingPlan.duration_weeks,
      };

      if (planId) {
        const { error } = await supabase
          .from('training_plans')
          .update(planPayload)
          .eq('id', planId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('training_plans')
          .insert({ ...planPayload, status: 'draft' })
          .select('id')
          .single();
        if (error) throw error;
        planId = data.id;
      }

      // Delete and reinsert days
      await supabase.from('training_plan_days').delete().eq('plan_id', planId);

      const daysPayload = editingPlan.days.map(d => ({
        plan_id: planId,
        day_number: d.day_number,
        routine_id: d.routine_id,
        label: d.label || null,
      }));

      if (daysPayload.length > 0) {
        const { error } = await supabase.from('training_plan_days').insert(daysPayload);
        if (error) throw error;
      }

      await onSuccess();
      setEditingPlan(null);
      showNotification('Plan guardado', 'success');
    } catch (error) {
      console.error('Error guardando plan:', error);
      showNotification('Error al guardar el plan', 'error');
    }
  };

  const deletePlan = async (id: string) => {
    const plan = trainingPlans.find(p => p.id === id);
    showConfirm(`¿Eliminar el plan "${plan?.name || 'este plan'}"?`, async () => {
      try {
        await supabase.from('training_plan_days').delete().eq('plan_id', id);
        const { error } = await supabase.from('training_plans').delete().eq('id', id);
        if (error) throw error;
        setTrainingPlans(prev => prev.filter(p => p.id !== id));
        showNotification('Plan eliminado', 'success');
      } catch (error) {
        console.error('Error eliminando plan:', error);
        showNotification('Error al eliminar el plan', 'error');
      }
    });
  };

  const activatePlan = async (id: string, onSuccess: () => Promise<void>) => {
    const activePlan = trainingPlans.find(p => p.status === 'active');
    if (activePlan && activePlan.id !== id) {
      showNotification('Ya tenés un plan activo. Completalo o abandonalo primero.', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('training_plans')
        .update({ status: 'active', start_date: getLocalDateString() })
        .eq('id', id);
      if (error) throw error;
      await onSuccess();
      showNotification('Plan activado', 'success');
    } catch (error) {
      console.error('Error activando plan:', error);
      showNotification('Error al activar el plan', 'error');
    }
  };

  const completePlan = async (id: string, onSuccess: () => Promise<void>) => {
    const doComplete = async () => {
      try {
        const { error } = await supabase
          .from('training_plans')
          .update({ status: 'completed' })
          .eq('id', id);
        if (error) throw error;
        await onSuccess();
        showNotification('Plan completado', 'success');
      } catch (error) {
        console.error('Error completando plan:', error);
        showNotification('Error al completar el plan', 'error');
      }
    };

    const plan = trainingPlans.find(p => p.id === id);
    if (!plan || !user) {
      await doComplete();
      return;
    }

    let weeksProgress = 1;
    if (plan.start_date) {
      const startDate = parseLocalDate(plan.start_date);
      weeksProgress = getTrainingPlanWeekNumber(startDate);
    }

    const plannedWeeks = plan.duration_weeks;
    const expectedWorkouts = plan.duration_weeks * plan.days_per_week;
    const isWeeksIncomplete = weeksProgress < plannedWeeks;

    try {
      const { count, error: countError } = await supabase
        .from('workouts')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('training_plan_id', id);
      if (countError) throw countError;

      const completedWorkouts = count ?? 0;
      const isWorkoutsIncomplete = completedWorkouts < expectedWorkouts;

      if (isWeeksIncomplete || isWorkoutsIncomplete) {
        const warningParts: string[] = [];
        if (isWeeksIncomplete) {
          warningParts.push(`Semana ${Math.min(weeksProgress, plannedWeeks)} de ${plannedWeeks}`);
        }
        if (isWorkoutsIncomplete) {
          warningParts.push(`${completedWorkouts}/${expectedWorkouts} entrenamientos`);
        }

        showConfirm(
          `El plan parece incompleto (${warningParts.join(' · ')}). ¿Querés marcarlo como completado igual?`,
          () => {
            void doComplete();
          }
        );
        return;
      }
    } catch (error) {
      console.error('Error verificando avance del plan:', error);
      // If verification fails, keep previous behavior and allow completion.
    }

    try {
      await doComplete();
    } catch {
      // handled in doComplete
    }
  };

  const abandonPlan = async (id: string, onSuccess: () => Promise<void>) => {
    const plan = trainingPlans.find(p => p.id === id);
    showConfirm(`¿Abandonar el plan "${plan?.name || 'este plan'}"?`, async () => {
      try {
        const { error } = await supabase
          .from('training_plans')
          .update({ status: 'abandoned' })
          .eq('id', id);
        if (error) throw error;
        await onSuccess();
        showNotification('Plan abandonado', 'success');
      } catch (error) {
        console.error('Error abandonando plan:', error);
        showNotification('Error al abandonar el plan', 'error');
      }
    });
  };

  // Derived functions

  const getActivePlan = useCallback((): TrainingPlan | undefined => {
    return trainingPlans.find(p => p.status === 'active');
  }, [trainingPlans]);

  const getActivePlanContext = useCallback((routineId: string): PlanContext | null => {
    const activePlan = trainingPlans.find(p => p.status === 'active');
    if (!activePlan || !activePlan.start_date) return null;

    const day = activePlan.days.find(d => d.routine_id === routineId);
    if (!day) return null;

    const startDate = parseLocalDate(activePlan.start_date);
    const weekNumber = Math.min(
      getTrainingPlanWeekNumber(startDate),
      activePlan.duration_weeks
    );

    return {
      planId: activePlan.id!,
      planName: activePlan.name,
      weekNumber: Math.max(1, weekNumber),
      totalWeeks: activePlan.duration_weeks,
      dayNumber: day.day_number,
      dayLabel: day.label,
    };
  }, [trainingPlans]);

  const getUnplannedRoutines = useCallback((routines: Routine[]): Routine[] => {
    const activePlan = trainingPlans.find(p => p.status === 'active');
    if (!activePlan) return routines;

    const plannedRoutineIds = new Set(activePlan.days.map(d => d.routine_id));
    return routines.filter(r => !plannedRoutineIds.has(r.id!));
  }, [trainingPlans]);

  return {
    trainingPlans,
    setTrainingPlans,
    plansLoading,
    editingPlan,
    setEditingPlan,
    loadTrainingPlans,
    createPlan,
    savePlan,
    deletePlan,
    activatePlan,
    completePlan,
    abandonPlan,
    getActivePlan,
    getActivePlanContext,
    getUnplannedRoutines,
  };
}
